import { Button, Chip } from '@sjuni/ui';
import { Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { type Column, ResourceTable } from '@/components/list/ResourceTable';
import { ResourceToolbar } from '@/components/list/ResourceToolbar';
import { useResourceAction, useResourceList } from '@/lib/api/resource';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { valueFor } from '@/lib/forms/translatable';
import { useListParams } from '@/lib/list/useListParams';

import { VocationalProgramEditor } from './VocationalProgramEditor';
import type { VocationalProgramRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/vocational/programs` and its editor.
 *
 * One route with a param rather than two screens: §2 writes it
 * `/vocational/programs · /:id`, and the list and the editor share nothing but
 * the resource name, so the split is a `useParams` check rather than a router
 * branch.
 */
export function VocationalProgramsPage() {
  /*
   * The splat, not a named param: the router registers each module as
   * `{path}/*` so that a detail route is gated by the module's permission
   * before its own screen decides what to do with the rest of the path. What
   * follows the module path is the record id, or nothing for the list.
   */
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <ProgramList /> : <VocationalProgramEditor recordId={rest} />;
}

function ProgramList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useEditorLocale();
  const list = useListParams('sort_order');

  const [deleting, setDeleting] = useState<VocationalProgramRow | null>(null);

  const { data, isPending, isError } = useResourceList<VocationalProgramRow>(
    'vocational-programs',
    list.params,
  );

  const act = useResourceAction<VocationalProgramRow>('vocational-programs');

  const columns: Column<VocationalProgramRow>[] = [
    {
      key: 'name',
      label: t('vocational.programs.columns.name'),
      render: (row) => (
        <span className="font-semibold text-primary">{valueFor(row.name, locale)}</span>
      ),
    },
    {
      key: 'status',
      label: t('vocational.programs.columns.status'),
      render: (row) => (
        <Chip tone={row.status === 'published' ? 'navy' : 'gold'}>
          {t(`editor.publishing.statuses.${row.status}`)}
        </Chip>
      ),
    },
    {
      key: 'translations',
      label: t('vocational.programs.columns.translations'),
      // §4.6's column set names `translations` for the news list, and the same
      // badge answers the same question here: what is missing in English.
      render: (row) => (
        <span className="text-caption uppercase text-secondary">
          {row.translations_available.join(', ') || '—'}
        </span>
      ),
    },
    {
      key: 'sort_order',
      label: t('vocational.programs.columns.order'),
      sortable: true,
      align: 'end',
      render: (row) => row.sort_order,
    },
    {
      key: 'actions',
      label: t('list.actions'),
      align: 'end',
      render: (row) =>
        row.deleted_at ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            iconLeft={<RotateCcw size={14} strokeWidth={1.75} aria-hidden="true" />}
            onClick={(event) => {
              event.stopPropagation();
              act.mutate({ id: row.id, action: 'restore' });
            }}
          >
            {t('list.restore')}
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            iconLeft={<Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />}
            onClick={(event) => {
              event.stopPropagation();
              setDeleting(row);
            }}
          >
            {t('common.delete')}
          </Button>
        ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.vocationalPrograms')}</h1>
        <p className="mt-1 text-body-sm text-secondary">{t('vocational.programs.intro')}</p>
      </div>

      {isError ? <FormError message={t('errors.generic')} /> : null}

      <ResourceToolbar
        search={list.search}
        onSearchChange={list.setSearch}
        perPage={list.perPage}
        onPerPageChange={list.setPerPage}
        showTrashed={list.showTrashed}
        onShowTrashedChange={list.setShowTrashed}
        meta={data?.meta}
        buildPageHref={list.buildPageHref}
        primaryAction={
          <Button
            type="button"
            size="sm"
            iconLeft={<Plus size={16} strokeWidth={1.75} aria-hidden="true" />}
            onClick={() => navigate('/vocational/programs/new')}
          >
            {t('vocational.programs.new')}
          </Button>
        }
      />

      <ResourceTable
        rows={data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={isPending}
        sort={list.sort}
        onSortChange={list.setSort}
        onOpen={(row) => navigate(`/vocational/programs/${row.id}`)}
        emptyTitle={t('vocational.programs.empty.title')}
        emptyBody={t('vocational.programs.empty.body')}
      />

      <ConfirmDialog
        open={deleting !== null}
        title={t('vocational.programs.delete.title')}
        body={t('vocational.programs.delete.body', {
          name: deleting ? valueFor(deleting.name, locale) : '',
        })}
        confirmLabel={t('common.delete')}
        // Soft-deleted, unlike the contacts — `vocational_programs` has a
        // `deleted_at`, so the "show deleted" toggle can bring it back.
        busy={act.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) {
            act.mutate(
              { id: deleting.id, action: 'delete' },
              { onSettled: () => setDeleting(null) },
            );
          }
        }}
      />
    </div>
  );
}
